'use client';

import { c as _c } from "react/compiler-runtime";
import type React from 'react';
import type { Theme } from 'src/Theme';
import EditOutlined from '@mui/icons-material/EditOutlined';
import { formatValue } from '@effektify/components/utils';
import { useDataProvider } from '@effektify/data-provider';
import Button from '@mui/material/Button';
import type { DialogProps } from '@mui/material/Dialog';
import { styled } from '@mui/material/styles';
import { useState } from 'react';
import { IconButton } from 'src/components/Atoms/IconButton';
import { FlexColumn } from 'src/components/Atoms/FlexBox';
import { Dialog } from 'src/components/Molecules/Dialog';
import { TextInput } from 'src/components/Molecules/TextInput';
import useReports from 'src/hooks/useReports';
import useTranslations from 'src/hooks/useTranslations';
type ValueType = 'integer' | 'float' | 'money' | 'percentage' | 'string' | 'duration';
type DialogTransition = NonNullable<NonNullable<DialogProps['slots']>['transition']>;
interface GoalEditButtonProps {
  goalType: string;
  goal?: number | null;
  description?: string | null;
  valueType: string;
  currency?: string;
  dialogTransition?: DialogTransition;
}
const StyledEditIcon = styled(EditOutlined)(({
  theme
}) => ({
  fontSize: theme.fonts.size.lg,
  color: theme.colors.icon.primary
}));
const GoalEditButton: React.FC<GoalEditButtonProps> = t0 => {
  const $ = _c(87);
  const {
    goalType,
    goal,
    description: t1,
    valueType,
    currency,
    dialogTransition
  } = t0;
  const description = t1 === undefined ? "" : t1;
  const {
    t
  } = useTranslations();
  const {
    updateGoal,
    deleteGoal
  } = useReports();
  const {
    form,
    formData
  } = useDataProvider();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(goal ?? 0);
  const [draft, setDraft] = useState(`${goal ?? 0}`);
  const [editing, setEditing] = useState(false);
  const [descriptionValue, setDescriptionValue] = useState(description ?? "");
  let t2;
  if ($[0] !== description || $[1] !== goal) {
    t2 = () => {
      setValue(goal ?? 0);
      setDraft(`${goal ?? 0}`);
      setEditing(false);
      setDescriptionValue(description ?? "");
      setOpen(true);
    };
    $[0] = description;
    $[1] = goal;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const openDialog = t2;
  let t3;
  if ($[3] !== form || $[4] !== formData?.group) {
    t3 = () => form && "group" in form ? formData?.group as string | undefined : undefined;
    $[3] = form;
    $[4] = formData?.group;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const getGroupId = t3;
  let t4;
  if ($[6] !== descriptionValue || $[7] !== getGroupId || $[8] !== goalType || $[9] !== updateGoal || $[10] !== value) {
    t4 = async result => {
      if (!result) {
        setOpen(false);
        return;
      }
      const groupId = getGroupId();
      await updateGoal(goalType, {
        goal: value,
        description: descriptionValue,
        ...(groupId ? {
          groupId
        } : {})
      });
      setOpen(false);
    };
    $[6] = descriptionValue;
    $[7] = getGroupId;
    $[8] = goalType;
    $[9] = updateGoal;
    $[10] = value;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const handleClose = t4;
  let t5;
  if ($[12] !== deleteGoal || $[13] !== getGroupId || $[14] !== goalType) {
    t5 = async () => {
      const groupId_0 = getGroupId();
      await deleteGoal(goalType, groupId_0 ? {
        groupId: groupId_0
      } : undefined);
      setOpen(false);
    };
    $[12] = deleteGoal;
    $[13] = getGroupId;
    $[14] = goalType;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const handleDelete = t5;
  const canDelete = goal != null || Boolean(description);
  let t6;
  if ($[16] !== currency || $[17] !== draft || $[18] !== editing || $[19] !== value || $[20] !== valueType) {
    t6 = editing ? draft : formatValue(value, {
      type: valueType as ValueType,
      currency,
      short: false
    });
    $[16] = currency;
    $[17] = draft;
    $[18] = editing;
    $[19] = value;
    $[20] = valueType;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const displayValue = t6;
  let t7;
  if ($[22] !== t) {
    t7 = t("components.goals.editGoalLabel");
    $[22] = t;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <StyledEditIcon />;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== openDialog || $[26] !== t7) {
    t9 = <IconButton ariaLabel={t7} onClick={openDialog}>{t8}</IconButton>;
    $[25] = openDialog;
    $[26] = t7;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== t) {
    t10 = t("components.goals.editGoalTitle");
    $[28] = t;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== dialogTransition) {
    t11 = dialogTransition ? {
      slots: {
        transition: dialogTransition
      }
    } : {};
    $[30] = dialogTransition;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== canDelete || $[33] !== handleDelete || $[34] !== t) {
    t12 = canDelete && <Button variant="text" color="secondary" onClick={handleDelete} sx={{
      marginRight: "auto"
    }}>{t("components.goals.deleteGoalLabel")}</Button>;
    $[32] = canDelete;
    $[33] = handleDelete;
    $[34] = t;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== handleClose) {
    t13 = () => handleClose(false);
    $[36] = handleClose;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] !== t) {
    t14 = t("common.cancel");
    $[38] = t;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== t13 || $[41] !== t14) {
    t15 = <Button variant="text" onClick={t13}>{t14}</Button>;
    $[40] = t13;
    $[41] = t14;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== handleClose) {
    t16 = () => handleClose(true);
    $[43] = handleClose;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== t) {
    t17 = t("common.ok");
    $[45] = t;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] !== t16 || $[48] !== t17) {
    t18 = <Button variant="contained" onClick={t16}>{t17}</Button>;
    $[47] = t16;
    $[48] = t17;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  let t19;
  if ($[50] !== t12 || $[51] !== t15 || $[52] !== t18) {
    t19 = <>{t12}{t15}{t18}</>;
    $[50] = t12;
    $[51] = t15;
    $[52] = t18;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  let t20;
  if ($[54] !== t) {
    t20 = t("components.goals.editGoalValueLabel");
    $[54] = t;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  const t21 = editing ? "number" : "text";
  let t22;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = {
      inputMode: "decimal"
    };
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  let t23;
  if ($[57] !== value) {
    t23 = () => {
      setEditing(true);
      setDraft(`${value}`);
    };
    $[57] = value;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  let t24;
  if ($[59] !== draft) {
    t24 = () => {
      setEditing(false);
      const parsed = parseFloat(draft);
      if (!isNaN(parsed)) {
        setValue(parsed);
      }
    };
    $[59] = draft;
    $[60] = t24;
  } else {
    t24 = $[60];
  }
  let t25;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = e => setDraft(e.target.value);
    $[61] = t25;
  } else {
    t25 = $[61];
  }
  let t26;
  if ($[62] !== displayValue || $[63] !== t20 || $[64] !== t21 || $[65] !== t23 || $[66] !== t24) {
    t26 = <TextInput label={t20} value={displayValue} type={t21} inputProps={t22} onFocus={t23} onBlur={t24} onChange={t25} />;
    $[62] = displayValue;
    $[63] = t20;
    $[64] = t21;
    $[65] = t23;
    $[66] = t24;
    $[67] = t26;
  } else {
    t26 = $[67];
  }
  let t27;
  if ($[68] !== t) {
    t27 = t("components.goals.editGoalDescriptionLabel");
    $[68] = t;
    $[69] = t27;
  } else {
    t27 = $[69];
  }
  let t28;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = e_0 => setDescriptionValue(e_0.target.value);
    $[70] = t28;
  } else {
    t28 = $[70];
  }
  let t29;
  if ($[71] !== descriptionValue || $[72] !== t27) {
    t29 = <TextInput label={t27} value={descriptionValue} onChange={t28} />;
    $[71] = descriptionValue;
    $[72] = t27;
    $[73] = t29;
  } else {
    t29 = $[73];
  }
  let t30;
  if ($[74] !== t26 || $[75] !== t29) {
    t30 = <FlexColumn sx={_temp}>{t26}{t29}</FlexColumn>;
    $[74] = t26;
    $[75] = t29;
    $[76] = t30;
  } else {
    t30 = $[76];
  }
  let t31;
  if ($[77] !== handleClose || $[78] !== open || $[79] !== t10 || $[80] !== t11 || $[81] !== t19 || $[82] !== t30) {
    t31 = <Dialog open={open} title={t10} handleClose={handleClose} {...t11} customFooter={t19}>{t30}</Dialog>;
    $[77] = handleClose;
    $[78] = open;
    $[79] = t10;
    $[80] = t11;
    $[81] = t19;
    $[82] = t30;
    $[83] = t31;
  } else {
    t31 = $[83];
  }
  let t32;
  if ($[84] !== t31 || $[85] !== t9) {
    t32 = <>{t9}{t31}</>;
    $[84] = t31;
    $[85] = t9;
    $[86] = t32;
  } else {
    t32 = $[86];
  }
  return t32;
};
export default GoalEditButton;
export type { GoalEditButtonProps };
function _temp(theme) {
  return {
    gap: theme.sizes.spacing["07"],
    paddingTop: theme.sizes.spacing["05"]
  };
}